/* The bar check (notorioushospitality.com/barsjekk), Norwegian and English. Version 1, 11 October 2026.
   The texts come from the page (snippet nh-barcheck-text, through #nh-bc-data). This file holds the structure:
   the questions and their areas, the scoring, the lead type and the English labels used in the lead email.
   Flow: start, 10 yes or no questions, 5 questions about the venue, contact details (the store contact form), result.
   Score: each yes is 10 points (0 to 100). Bands: 80 and up "well set up", 50 to 79 "solid foundation", below 50 "room to grow".
   Areas: speed (4 questions), consistency (3), pour cost (3). An area is strong from 75 %, middle from 50 %, weak below.
   The three insights take one area each, weakest first: a headline for the level, then the tip for the first "no" in that area.
   Lead type (sent in the email, never shown): points for cocktails per week (0 to 3), venues (0 to 2) and help wanted
   (products 0, plus recipes and costing 1, whole program 2). "Just looking" is cold, unless the venue sells 300+ cocktails
   a week or runs more than one venue (then warm). Otherwise hot from 3 points, or from 2 points with a score under 60; the rest warm.
   Hot gets a tasting on the result page, warm the ordering path, cold the mixer guide and the price list.
   The tasting depends on where the venue is: Oslo and Akershus book a time on the booking page (D.bookingUrl), with the
   tasting request form as the fallback; the rest of Norway and abroad ask for free samples (abroad without Super Juice).
   Every result ends with the downloads panel (mixer guide, price list, catalog) and its send-by-email form.
   The answers are kept in session storage, so the result can be shown after the form returns with ?check=sent
   (or ?tasting=sent, ?samples=sent, ?docs=sent). Version 2, 17 October 2026.
   Version 3, 31 October 2026: a photo and icon per area beside each question, a progress bar in four parts, yes and no
   buttons with icons, a score that counts up, and the bonus (free garnish on the first order, D.offer) right under the score,
   for venues in Norway that have not ordered before. Every ordering button goes to the quick professional account
   (D.applyUrl) or, for a logged-in venue or lead, the order form (D.orderUrl), and saves name, email, phone and venue in
   localStorage "nh-bc-prefill" for snippet nh-bc-prefill, which fills step 1 of the quick account with them.
   Version 4, 1 November 2026: 17 questions. The venue part asks cocktails per week (under 50, 50 to 100, 100 to 200, over 200),
   cocktail price on the menu (sets the starting price on the result page), venues, venue type, who makes most drinks on a busy night, the main glass, and blender or slush machine.
   Every menu comes with an optional batching guide (closed by default): about 3 times faster than from scratch, time saved per week from
   the cocktails-per-week answer (60 seconds from scratch, 20 batched), what to batch ahead and add when serving, and what batching needs.
   The result shows "the menu made for you": six drinks from the drink library (proven recipes only, house spirits, list
   prices), picked by staff level (waiter: level 1, bartender: up to 2, trained bartender and mixologist: up to 3), glass,
   blender and venue type, plus one alcohol-free twin. Lead type: hot (the 30-minute meeting) from 100 cocktails a week, more
   than one venue, or a mixologist; everyone else warm (start ordering). The goal, obstacle and help questions are gone. */
(function () {
  'use strict';
  var dataEl = document.getElementById('nh-bc-data');
  var app = document.querySelector('[data-bc-app]');
  var intro = document.querySelector('[data-bc-intro]');
  if (!dataEl || !app || !intro || app.getAttribute('data-ready')) return;
  app.setAttribute('data-ready', '1');
  var D;
  try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }
  var T = D.t || {};
  var KEY = 'nh-bc-v1';
  var SRC_KEY = 'nh-bc-kilde';
  var PREFILL_KEY = 'nh-bc-prefill';
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var ORDER_URL = D.canOrder ? D.orderUrl : D.applyUrl;

  var QUESTIONS = [
    { id: 't1', area: 'speed', label: 'Prep finished before service' },
    { id: 't2', area: 'speed', label: 'Most cocktails made in under a minute' },
    { id: 't3', area: 'speed', label: 'Batches or pre-mixes best sellers' },
    { id: 't4', area: 'speed', label: 'Gets through a night without running out' },
    { id: 'k1', area: 'consistency', label: 'Written spec for every drink' },
    { id: 'k2', area: 'consistency', label: 'Everyone uses a jigger' },
    { id: 'k3', area: 'consistency', label: 'Same drink from the newest bartender' },
    { id: 'v1', area: 'cost', label: 'Knows pour cost of top 3 cocktails' },
    { id: 'v2', area: 'cost', label: 'Costed recipes in the till' },
    { id: 'v3', area: 'cost', label: 'Knows weekly citrus and garnish waste' }
  ];
  var AREAS = ['speed', 'consistency', 'cost'];
  var QUALIFIERS = [
    { id: 'volume', options: [
      { v: 'under-50', en: 'Under 50', pts: 0 }, { v: '50-100', en: '50 to 100', pts: 1 },
      { v: '100-200', en: '100 to 200', pts: 2 }, { v: 'over-200', en: 'Over 200', pts: 3 }] },
    /* Cocktail price: typed in kroner incl. MVA on the number keypad (digits only, 50 to 999), or "No cocktail menu yet". */
    { id: 'price', input: 'kr', options: [{ v: 'none', en: 'No cocktail menu yet' }] },
    { id: 'venues', options: [
      { v: '1', en: 'One', pts: 0 }, { v: '2-3', en: '2 to 3', pts: 1 }, { v: '4+', en: '4 or more', pts: 2 }] },
    { id: 'venueType', options: [
      { v: 'bar', en: 'Bar' }, { v: 'restaurant', en: 'Restaurant' }, { v: 'hotel', en: 'Hotel' },
      { v: 'nightclub', en: 'Nightclub' }, { v: 'other', en: 'Other' }] },
    { id: 'staff', options: [
      { v: 'waiter', en: 'Waiter or runner' }, { v: 'bartender', en: 'Bartender' },
      { v: 'trained', en: 'Trained bartender' }, { v: 'mixologist', en: 'Mixologist' }] },
    { id: 'glass', options: [
      { v: 'highball', en: 'Highball' }, { v: 'rocks', en: 'Rocks' }, { v: 'wine', en: 'Wine glass' }, { v: 'coupe', en: 'Coupe or martini' }] },
    { id: 'blender', options: [
      { v: 'yes', en: 'Blender or slush machine: yes' }, { v: 'no', en: 'Blender or slush machine: no' }] }
  ];
  /* The drink library: proven recipes only (served at our customer venues), house spirits, standard list prices excl. MVA,
     1 November 2026. p: [ingredient, cl or pieces], mix = the drink's mixer. Built from build_library.py. */
  var DRINKS = [{"n":"Strawberry & Rhubarb Gin Sour","m":"SR","s":"Sour","g":"rocks","l":3,"af":false,"c":35.45,"p":[["mix",5.0],["hGin",4.0],["egg",2.5]],"tw":"Strawberry & Rhubarb Lemonade"},{"n":"Strawberry & Rhubarb Spritz","m":"SR","s":"Spritz","g":"wine","l":1,"af":false,"c":31.79,"p":[["mix",6.0],["hProsecco",10.0],["soda",3.0]],"tw":""},{"n":"Strawberry & Rhubarb Lemonade","m":"SR","s":"Lemonade","g":"highball","l":1,"af":true,"c":18.36,"p":[["mix",7.0],["soda",12.0]],"tw":""},{"n":"Passionfruit & Vanilla Vodka Sour","m":"PV","s":"Sour","g":"rocks","l":3,"af":false,"c":33.97,"p":[["mix",5.0],["hVodka",4.0],["egg",2.5]],"tw":"Passionfruit & Vanilla Lemonade"},{"n":"Passionfruit & Vanilla Lemonade","m":"PV","s":"Lemonade","g":"highball","l":1,"af":true,"c":18.94,"p":[["mix",7.0],["soda",12.0]],"tw":""},{"n":"Coffee & Tonka Vodka Classic","m":"CT","s":"Classic","g":"rocks","l":2,"af":false,"c":30.06,"p":[["mix",6.0],["hVodka",4.0]],"tw":"Coffee & Tonka Lemonade"},{"n":"Coffee & Tonka Vodka Frozen","m":"CT","s":"Frozen","g":"any","l":2,"af":false,"c":32.06,"p":[["mix",7.0],["hVodka",4.0]],"tw":"Coffee & Tonka Lemonade"},{"n":"Tropical Spice Whisky Sour","m":"TS","s":"Sour","g":"rocks","l":3,"af":false,"c":37.34,"p":[["mix",5.0],["hWhisky",4.0],["egg",2.5]],"tw":"Tropical Spice Lemonade"},{"n":"Tropical Spice Tequila Highball","m":"TS","s":"Highball","g":"highball","l":1,"af":false,"c":31.45,"p":[["mix",5.0],["hTequila",3.0],["soda",10.0]],"tw":"Tropical Spice Lemonade"},{"n":"Tropical Spice Tequila Daisy","m":"TS","s":"Daisy","g":"coupe","l":3,"af":false,"c":32.23,"p":[["mix",3.0],["limeSJ",1.0],["hTriple",2.0],["hTequila",2.0]],"tw":"Tropical Spice Lemonade"},{"n":"Tropical Spice Lemonade","m":"TS","s":"Lemonade","g":"highball","l":1,"af":true,"c":20.25,"p":[["mix",7.0],["soda",12.0]],"tw":""},{"n":"Mint & Lime Lemonade","m":"ML","s":"Lemonade","g":"highball","l":1,"af":true,"c":17.47,"p":[["mix",7.0],["soda",12.0]],"tw":""},{"n":"Tropical Spice Paloma","m":"TS","s":"Highball","g":"highball","l":1,"af":false,"c":33.35,"p":[["mix",3.0],["hTequila",3.5],["limeSJ",1.0],["grapefruitSoda",8.0]],"tw":"Tropical Spice Lemonade"},{"n":"Passionfruit Mule","m":"PV","s":"Highball","g":"highball","l":1,"af":false,"c":30.0,"p":[["mix",3.0],["hVodka",3.5],["limeSJ",1.0],["gingerBeer",8.0]],"tw":"Passionfruit & Vanilla Lemonade"},{"n":"Tropical Mule","m":"TS","s":"Highball","g":"highball","l":1,"af":false,"c":30.71,"p":[["mix",3.0],["hRum",3.5],["limeSJ",1.0],["gingerBeer",6.0]],"tw":"Tropical Spice Lemonade"},{"n":"Bitter Strawberry Spritz","m":"SR","s":"Spritz","g":"wine","l":1,"af":false,"c":34.26,"p":[["mix",4.0],["hBitter",2.5],["hProsecco",8.0]],"tw":"Strawberry & Rhubarb Lemonade"},{"n":"Mint & Lime Hugo Spritz","m":"ML","s":"Spritz","g":"wine","l":1,"af":false,"c":33.82,"p":[["mix",4.0],["hTriple",2.0],["hProsecco",8.0]],"tw":"Mint & Lime Lemonade"},{"n":"Passionfruit Spritz","m":"PV","s":"Spritz","g":"wine","l":1,"af":false,"c":33.28,"p":[["mix",3.0],["hTriple",2.0],["hProsecco",8.0]],"tw":"Passionfruit & Vanilla Lemonade"},{"n":"Mint & Lime Mojito","m":"ML","s":"Highball","g":"highball","l":1,"af":false,"c":29.98,"p":[["mix",4.5],["hRum",4.0],["soda",6.0]],"tw":"Mint & Lime Lemonade"}];
  var MIXER_NAME = { SR: 'Strawberry & Rhubarb', PV: 'Passionfruit & Vanilla', CT: 'Coffee & Tonka', TS: 'Tropical Spice', ML: 'Mint & Lime' };
  var MAX_LEVEL = { waiter: 1, bartender: 2, trained: 3, mixologist: 3 };
  var TOTAL = QUESTIONS.length + QUALIFIERS.length;
  var SEG_LABEL = { hot: 'Hot: offer the 30-minute meeting', warm: 'Warm: start ordering' };
  var BAND_LABEL = { high: 'well set up', mid: 'solid foundation', low: 'room to grow' };
  /* The four parts of the progress bar, in question order. */
  var GROUPS = [
    { key: 'speed', start: 0, n: 4 }, { key: 'consistency', start: 4, n: 3 },
    { key: 'cost', start: 7, n: 3 }, { key: 'venue', start: 10, n: QUALIFIERS.length }
  ];
  /* Line icons per area (stopwatch, jigger, coins, bar counter), drawn in currentColor. */
  var SVG = '<svg viewBox="0 0 32 32" width="30" height="30" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" focusable="false">';
  var ICONS = {
    speed: SVG + '<circle cx="16" cy="18" r="10"/><path d="M16 18l5-4M13 4h6M16 4v4M25 9l2-2"/></svg>',
    consistency: SVG + '<path d="M9 4h14l-5 11h-4zM14 15h4M11 28h10l-3-13h-4z"/></svg>',
    cost: SVG + '<ellipse cx="13" cy="9" rx="8" ry="3.5"/><path d="M5 9v5c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5V9"/><path d="M11 18.5v4c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5v-5c0-1.9-3.6-3.5-8-3.5"/></svg>',
    venue: SVG + '<path d="M4 14h24M6 14v14M26 14v14M4 28h24M10 14V6h4v8M18 14V4h4v10"/></svg>'
  };
  var ICON_YES = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7"/></svg>';
  var ICON_NO = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var ICON_GIFT = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="8" width="18" height="4" rx=".5"/><path d="M5 12v8h14v-8M12 8v12"/><path d="M12 8c-1.5-3-5-3.6-5-1.4C7 8 9.5 8 12 8zM12 8c1.5-3 5-3.6 5-1.4C17 8 14.5 8 12 8z"/></svg>';

  var quiz = app.querySelector('[data-bc-quiz]');
  var stepBox = app.querySelector('[data-bc-step]');
  var progText = app.querySelector('[data-bc-progress-text]');
  var segsBox = app.querySelector('[data-bc-segs]');
  var media = app.querySelector('[data-bc-media]');
  var mediaIcon = app.querySelector('[data-bc-icon]');
  var mediaLabel = app.querySelector('[data-bc-media-label]');
  var gate = app.querySelector('[data-bc-gate]');
  var form = document.getElementById('nh-bc-form');
  var resultBox = app.querySelector('[data-bc-result]');
  var tpl = app.querySelector('[data-bc-tasting-tpl]');
  var tasting = tpl ? tpl.querySelector('[data-bc-tasting]') : null;
  var tastingForm = document.getElementById('nh-bc-tasting-form');
  var samples = tpl ? tpl.querySelector('[data-bc-samples]') : null;
  var samplesForm = document.getElementById('nh-bc-samples-form');
  var docs = tpl ? tpl.querySelector('[data-bc-docs]') : null;
  var docsForm = document.getElementById('nh-bc-docs-form');
  var PARTS = [
    { el: tasting, form: tastingForm, ok: '[data-bc-tasting-ok]' },
    { el: samples, form: samplesForm, ok: '[data-bc-samples-ok]' },
    { el: docs, form: docsForm, ok: '[data-bc-docs-ok]' }
  ];
  if (!quiz || !stepBox || !gate || !form || !resultBox) return;

  /* ---------- helpers ---------- */
  function fresh(contact) { return { v: 1, answers: {}, step: 0, contact: contact || {}, sent: false }; }
  function load() {
    try {
      var s = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');
      return s && s.v === 1 && s.answers ? s : null;
    } catch (e) { return null; }
  }
  function save() { try { window.sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {} }
  function fmt(s, o) {
    return String(s == null ? '' : s).replace(/\{(\w+)\}/g, function (m, k) { return o && o[k] != null ? o[k] : m; });
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function params() {
    var o = {};
    location.search.replace(/^\?/, '').split('&').forEach(function (kv) {
      if (!kv) return;
      var i = kv.indexOf('=');
      try {
        o[decodeURIComponent(i < 0 ? kv : kv.slice(0, i)).toLowerCase()] =
          i < 0 ? '' : decodeURIComponent(kv.slice(i + 1).replace(/\+/g, ' '));
      } catch (e) {}
    });
    return o;
  }
  function source() {
    try { var s = window.sessionStorage.getItem(SRC_KEY); if (s) return s; } catch (e) {}
    var q = params();
    return String(q.kilde || q.utm_source || 'Direct').replace(/[^\w .:-]/g, '').slice(0, 60) || 'Direct';
  }
  function qualifier(id) {
    for (var i = 0; i < QUALIFIERS.length; i++) if (QUALIFIERS[i].id === id) return QUALIFIERS[i];
    return null;
  }
  function option(id) {
    var Q = qualifier(id), v = state.answers[id];
    if (!Q) return null;
    for (var i = 0; i < Q.options.length; i++) if (Q.options[i].v === v) return Q.options[i];
    return null;
  }
  function pts(id) { var o = option(id); return o && o.pts ? o.pts : 0; }
  function focusEl(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (e) { try { el.focus(); } catch (e2) {} }
  }
  function toTop() {
    var y = app.getBoundingClientRect().top + window.pageYOffset - 76;
    if (window.pageYOffset > y) window.scrollTo(0, Math.max(0, y));
  }
  function setBusy(btn, on, label) {
    if (!btn) return;
    if (on) {
      if (!btn.getAttribute('data-label')) btn.setAttribute('data-label', btn.innerHTML);
      btn.disabled = true;
      btn.textContent = T.sending || '…';
      // Shopify's spam check (hCaptcha) may pause the form for a challenge; give the button back if it is closed.
      setTimeout(function () { setBusy(btn, false); }, 8000);
    } else {
      btn.disabled = false;
      if (btn.getAttribute('data-label')) btn.innerHTML = btn.getAttribute('data-label');
    }
  }

  /* ---------- scoring ---------- */
  function compute() {
    var a = state.answers, per = {}, yes = 0;
    AREAS.forEach(function (ar) { per[ar] = { yes: 0, total: 0, firstNo: null }; });
    QUESTIONS.forEach(function (q) {
      var p = per[q.area];
      p.total += 1;
      if (a[q.id] === 'yes') { p.yes += 1; yes += 1; }
      else if (!p.firstNo) p.firstNo = q.id;
    });
    AREAS.forEach(function (ar) {
      var p = per[ar];
      p.pct = p.total ? p.yes / p.total : 0;
      p.level = p.pct >= 0.75 ? 'strong' : (p.pct >= 0.5 ? 'middle' : 'weak');
    });
    var score = Math.round(yes / QUESTIONS.length * 100);
    var band = score >= 80 ? 'high' : (score >= 50 ? 'mid' : 'low');
    var vol = pts('volume'), ven = pts('venues'), fit = vol + ven;
    var seg = (vol >= 2 || ven >= 1 || a.staff === 'mixologist') ? 'hot' : 'warm';
    return { score: score, band: band, per: per, seg: seg, fit: fit };
  }
  function complete() {
    return QUESTIONS.every(function (q) { return state.answers[q.id]; }) &&
      QUALIFIERS.every(function (Q) { return state.answers[Q.id]; });
  }

  /* ---------- lead email fields (English, for the inbox and the pipeline) ---------- */
  function fillOutputs() {
    var r = compute();
    var out = {
      lead: SEG_LABEL[r.seg] + ' (fit points ' + r.fit + ')',
      score: r.score + ' % (' + BAND_LABEL[r.band] + ')',
      speed: r.per.speed.yes + ' of ' + r.per.speed.total,
      consistency: r.per.consistency.yes + ' of ' + r.per.consistency.total,
      cost: r.per.cost.yes + ' of ' + r.per.cost.total,
      answers: QUESTIONS.map(function (q) {
        var v = state.answers[q.id];
        return q.label + ': ' + (v === 'yes' ? 'Yes' : (v === 'no' ? 'No' : 'not answered'));
      }).join('\n'),
      source: source(),
      menu: pickMenu().map(function (d) { return d.n; }).join(', ')
    };
    QUALIFIERS.forEach(function (Q) { var o = option(Q.id); out[Q.id] = o ? o.en : 'not answered'; });
    if (priceFromAnswer()) out.price = priceFromAnswer() + ' kr incl. MVA';
    Array.prototype.forEach.call(form.querySelectorAll('[data-bc-out]'), function (el) {
      var k = el.getAttribute('data-bc-out');
      if (k in out) el.value = out[k];
    });
    return r;
  }
  function readContact() {
    var c = state.contact || {};
    Array.prototype.forEach.call(form.querySelectorAll('[data-bc-in]'), function (el) {
      var k = el.getAttribute('data-bc-in');
      c[k] = el.type === 'checkbox' ? el.checked : el.value;
    });
    state.contact = c;
  }
  function restoreContact() {
    var c = state.contact || {};
    Array.prototype.forEach.call(form.querySelectorAll('[data-bc-in]'), function (el) {
      var k = el.getAttribute('data-bc-in');
      if (!(k in c)) return;
      if (el.type === 'checkbox') el.checked = !!c[k];
      else if (!el.value) el.value = c[k] || '';
    });
  }

  /* ---------- views ---------- */
  function show(which) {
    var inApp = which !== 'intro';
    intro.hidden = inApp;
    app.hidden = !inApp;
    quiz.hidden = which !== 'quiz';
    gate.hidden = which !== 'gate';
    resultBox.hidden = which !== 'result';
    document.documentElement.classList.toggle('nh-bc-in-quiz', which !== 'intro');
  }
  function opt(v, label, on, wide, icon) {
    return '<button type="button" class="nh-bc-opt' + (wide ? ' nh-bc-opt--wide' : ' nh-bc-opt--' + v) + '" data-v="' + esc(v) +
      '" aria-pressed="' + (on ? 'true' : 'false') + '">' + (icon ? '<span class="nh-bc-opt__icon">' + icon + '</span>' : '') +
      '<span>' + esc(label) + '</span></button>';
  }
  function buildSegs() {
    if (!segsBox || segsBox.childNodes.length) return;
    segsBox.innerHTML = GROUPS.map(function (g) {
      return '<div class="nh-bc-seg" style="flex:' + g.n + ' 1 0" data-seg="' + g.key + '">' +
        '<span class="nh-bc-seg__bar"><i></i></span><span class="nh-bc-seg__label">' + esc(T.areas && T.areas[g.key]) + '</span></div>';
    }).join('');
  }
  function paintSegs(i) {
    if (!segsBox) return;
    GROUPS.forEach(function (g) {
      var el = segsBox.querySelector('[data-seg="' + g.key + '"]');
      if (!el) return;
      var done = Math.max(0, Math.min(g.n, i - g.start));
      el.querySelector('i').style.width = Math.round(done / g.n * 100) + '%';
      el.classList.toggle('is-on', i >= g.start && i < g.start + g.n);
      el.classList.toggle('is-done', i >= g.start + g.n);
    });
  }
  function setMedia(area) {
    if (!media) return;
    var src = D.img && D.img[area];
    if (src && media.getAttribute('data-area') !== area) {
      media.setAttribute('data-area', area);
      quiz.setAttribute('data-area', area);
      if (REDUCED) media.src = src;
      else {
        media.classList.add('is-swap');
        setTimeout(function () { media.src = src; }, 120);
        media.onload = function () { media.classList.remove('is-swap'); };
        setTimeout(function () { media.classList.remove('is-swap'); }, 700);
      }
    }
    if (mediaIcon) mediaIcon.innerHTML = ICONS[area] || '';
    if (mediaLabel) mediaLabel.textContent = (T.areas && T.areas[area]) || '';
  }
  function preload() {
    if (!D.img) return;
    Object.keys(D.img).forEach(function (k) { var im = new Image(); im.src = D.img[k]; });
  }
  function renderStep() {
    var i = state.step;
    if (i >= TOTAL) { showGate(); return; }
    show('quiz');
    buildSegs();
    paintSegs(i);
    progText.textContent = fmt(T.progress, { n: i + 1, total: TOTAL });
    var html, area;
    if (i < QUESTIONS.length) {
      var q = QUESTIONS[i], cur = state.answers[q.id];
      area = q.area;
      html = '<fieldset class="nh-bc-q" data-q="' + q.id + '">' +
        '<legend class="nh-bc-q__legend"><span class="nh-bc-q__area">' + esc(T.areas && T.areas[q.area]) + '</span>' +
        '<span class="nh-bc-q__text" tabindex="-1" data-bc-qtext>' + esc(T.questions && T.questions[q.id]) + '</span></legend>' +
        '<div class="nh-bc-q__answers nh-bc-q__answers--yn">' +
        opt('yes', T.yes, cur === 'yes', false, ICON_YES) + opt('no', T.no, cur === 'no', false, ICON_NO) + '</div></fieldset>';
    } else {
      var Q = QUALIFIERS[i - QUESTIONS.length], qt = (T.qualifiers && T.qualifiers[Q.id]) || { o: {} }, cur2 = state.answers[Q.id];
      area = 'venue';
      html = '<fieldset class="nh-bc-q" data-q="' + Q.id + '">' +
        '<legend class="nh-bc-q__legend"><span class="nh-bc-q__area">' + esc(T.areas && T.areas.venue) + '</span>' +
        '<span class="nh-bc-q__text" tabindex="-1" data-bc-qtext>' + esc(qt.q) + '</span></legend>' +
        (Q.input === 'kr' ?
          '<form class="nh-bc-num" data-bc-num novalidate><div class="nh-bc-num__row">' +
            '<label class="nh-bc-num__field"><input type="text" inputmode="numeric" pattern="[0-9]*" maxlength="3" autocomplete="off" enterkeyhint="next"' +
            ' aria-label="' + esc(qt.q) + '" placeholder="' + esc(qt.ph || '179') + '" value="' + (/^\d+$/.test(cur2 || '') ? cur2 : '') + '" data-bc-num-in>' +
            '<span>' + esc(qt.unit) + '</span></label>' +
            '<button type="submit" class="nh-btn nh-btn--blue nh-bc-num__next">' + esc(qt.next) + '</button></div>' +
            '<p class="nh-bc-num__err" role="alert" data-bc-num-err></p></form>' +
            '<div class="nh-bc-q__answers">' + opt('none', qt.o.none || 'No cocktail menu yet', cur2 === 'none', true) + '</div>' :
          '<div class="nh-bc-q__answers' + (Q.options.length > 3 ? ' nh-bc-q__answers--grid' : '') + '">' + Q.options.map(function (o) {
            return opt(o.v, qt.o[o.v] || o.en, cur2 === o.v, true);
          }).join('') + '</div>') + '</fieldset>';
    }
    setMedia(area);
    stepBox.innerHTML = html;
    if (!REDUCED) {
      var fs = stepBox.firstChild;
      if (fs) { fs.classList.add('is-in'); requestAnimationFrame(function () { requestAnimationFrame(function () { fs.classList.remove('is-in'); }); }); }
    }
    toTop();
    focusEl(stepBox.querySelector('[data-bc-num-in]') || stepBox.querySelector('[data-bc-qtext]'));
  }
  function track(name, once) { if (window.NHDrinkCard) window.NHDrinkCard.track(name, once); }
  function showGate() {
    track('questions_done', true);
    show('gate');
    fillOutputs();
    restoreContact();
    toTop();
    focusEl(gate.querySelector('[data-bc-gate-title]'));
  }
  function start() {
    track('check_start', true);
    if (state.sent || complete()) state = fresh(state.contact);
    if (state.step >= TOTAL) state.step = 0;
    save();
    preload();
    renderStep();
  }

  var GAUGE_L = 282.74;
  function needle(score) {
    var ang = Math.PI * (1 - score / 100);
    return { x: (110 + 70 * Math.cos(ang)).toFixed(1), y: (110 - 70 * Math.sin(ang)).toFixed(1) };
  }
  function gauge(score) {
    var dash = (score / 100 * GAUGE_L).toFixed(1), n = needle(score);
    return '<svg class="nh-bc-gauge" viewBox="0 0 220 132" role="img" aria-label="' + esc(fmt(T.scoreAria, { score: score })) + '">' +
      '<path d="M20 110 A90 90 0 0 1 200 110" fill="none" stroke="#EBE7DF" stroke-width="18"></path>' +
      '<path class="nh-bc-gauge__val" d="M20 110 A90 90 0 0 1 200 110" fill="none" stroke="#437BD9" stroke-width="18" stroke-dasharray="' +
      dash + ' 283"></path>' +
      '<line class="nh-bc-gauge__needle" x1="110" y1="110" x2="' + n.x + '" y2="' + n.y + '" stroke="#111111" stroke-width="5" stroke-linecap="round"></line>' +
      '<circle cx="110" cy="110" r="9" fill="#111111"></circle>' +
      '<text x="20" y="131" text-anchor="middle" font-size="11" fill="#5C5852">0</text>' +
      '<text x="200" y="131" text-anchor="middle" font-size="11" fill="#5C5852">100</text></svg>';
  }
  /* The score counts up from 0, with the gauge and the needle following. */
  function countUp(score) {
    var val = resultBox.querySelector('.nh-bc-gauge__val');
    var nd = resultBox.querySelector('.nh-bc-gauge__needle');
    var num = resultBox.querySelector('[data-bc-num]');
    var bars = resultBox.querySelectorAll('.nh-bc-mini__bar span');
    if (!val || !nd || !num || REDUCED || !window.requestAnimationFrame) return;
    var t0 = null, dur = 1100;
    Array.prototype.forEach.call(bars, function (b) { b.setAttribute('data-w', b.style.width); b.style.width = '0%'; });
    function frame(t) {
      if (t0 === null) t0 = t;
      var p = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - p, 3), s = score * e, n = needle(s);
      val.setAttribute('stroke-dasharray', (s / 100 * GAUGE_L).toFixed(1) + ' 283');
      nd.setAttribute('x2', n.x); nd.setAttribute('y2', n.y);
      num.textContent = Math.round(s);
      if (p < 1) requestAnimationFrame(frame);
    }
    val.setAttribute('stroke-dasharray', '0 283');
    num.textContent = '0';
    requestAnimationFrame(frame);
    setTimeout(function () {
      Array.prototype.forEach.call(bars, function (b) { b.style.width = b.getAttribute('data-w'); });
    }, 250);
  }

  function zone(c) {
    if (c.location === 'Oslo' || c.location === 'Akershus') return 'near';
    if (c.location === 'Outside Norway') return 'abroad';
    return 'far';
  }
  /* The bonus: the new customer offer, for venues in Norway that have not ordered before. */
  function bonusHtml(c) {
    var B = T.bonus || {}, O = D.offer || {};
    if (!O.show || zone(c) === 'abroad') return '';
    var mins = B.min || {};
    var min = c.location === 'Oslo' ? mins.Oslo : (c.location === 'Akershus' ? mins.Akershus : mins.far);
    var bags = (O.bags || []).map(function (b) {
      return '<li>' + (b.img ? '<img src="' + esc(b.img) + '" width="150" height="150" loading="lazy" alt="">' : '') +
        '<span>' + esc(b.name) + '</span></li>';
    }).join('');
    return '<div class="nh-bc__panel nh-bc-bonus" id="nh-bc-bonus">' +
      '<div class="nh-bc-bonus__head"><span class="nh-bc-bonus__gift" aria-hidden="true">' + ICON_GIFT + '</span>' +
      '<span class="nh-bc-tape">' + esc(B.tape) + '</span></div>' +
      '<h2 class="nh-display nh-bc-bonus__title">' + esc(B.title) + '</h2>' +
      '<p class="nh-bc-bonus__text">' + esc(fmt(B.text, { min: min })) + '</p>' +
      '<ul class="nh-bc-bags nh-bc-bags--sm">' + bags + '</ul>' +
      '<p class="nh-bc-bags__note">' + esc(B.bag) + '</p>' +
      '<div class="nh-bc-next__ctas"><a class="nh-btn nh-btn--blue" href="' + esc(ORDER_URL) + '" data-bc-order>' +
        esc(D.canOrder ? B.btnIn : B.btn) + '</a></div>' +
      '<p class="nh-bc-small">' + esc(D.canOrder ? B.noteIn : B.note) + '</p></div>';
  }

  function renderResult(opts) {
    opts = opts || {};
    var r = compute(), c = state.contact || {}, band = (T.bands && T.bands[r.band]) || {};
    show('result');
    var rows = AREAS.map(function (ar) {
      var p = r.per[ar];
      return '<li><span class="nh-bc-mini__name">' + esc(T.areas[ar]) + '</span>' +
        '<span class="nh-bc-mini__bar" aria-hidden="true"><span style="width:' + Math.round(p.pct * 100) + '%"></span></span>' +
        '<span class="nh-bc-mini__val">' + esc(fmt(T.areaOf, { n: p.yes, total: p.total })) + '</span></li>';
    }).join('');
    var order = AREAS.slice().sort(function (a, b) { return r.per[a].pct - r.per[b].pct; });
    var insights = order.map(function (ar) {
      var p = r.per[ar], at = T.areaText[ar] || {};
      var body = p.firstNo ? T.tips[p.firstNo] : at.allYes;
      return '<li class="nh-bc-insight nh-bc-insight--' + p.level + '">' +
        '<span class="nh-bc-insight__icon" aria-hidden="true">' + ICONS[ar] + '</span><div>' +
        '<p class="nh-bc-insight__area">' + esc(T.areas[ar]) + ' &middot; ' + esc(fmt(T.areaOf, { n: p.yes, total: p.total })) + '</p>' +
        '<h3 class="nh-bc-insight__title">' + esc(at[p.level]) + '</h3><p>' + esc(body) + '</p></div></li>';
    }).join('');
    var head = c.venue ? fmt(T.resultFor, { venue: c.venue }) : '';
    resultBox.innerHTML = menuHtml(c) +
      '<div class="nh-bc__panel nh-bc-score">' +
        (head ? '<p class="nh-eyebrow">' + esc(head) + '</p>' : '') +
        '<div class="nh-bc-score__grid">' +
          '<div class="nh-bc-score__dial">' + gauge(r.score) +
            '<p class="nh-bc-score__num" aria-hidden="true"><span class="nh-display" data-bc-num>' + r.score + '</span><span class="nh-bc-score__unit">%</span></p></div>' +
          '<div class="nh-bc-score__text">' +
            '<h2 class="nh-display nh-bc-score__label">' + esc(band.label) + '</h2>' +
            '<p>' + esc(band.text) + '</p><ul class="nh-bc-mini">' + rows + '</ul></div>' +
        '</div>' +
      '</div>' +
      bonusHtml(c) +
      '<div class="nh-bc-insights"><h2 class="nh-display nh-bc-insights__title">' + esc(T.insightsTitle) + '</h2>' +
        '<ol class="nh-bc-insights__list">' + insights + '</ol></div>' +
      nextHtml(r, c) +
      '<div data-bc-docs-slot></div>' +
      '<div class="nh-bc-result__actions">' +
        '<button type="button" class="nh-btn nh-btn--sm" data-bc-retake>' + esc(T.retake) + '</button>' +
        '<button type="button" class="nh-btn nh-btn--sm" data-bc-print>' + esc(T.print) + '</button></div>' +
      '<p class="nh-bc-motto nh-bc-motto--result">' + esc(T.motto) + ' <span lang="en">We take care of the prep, you take care of the guests.</span></p>';
    mountParts(r, c, opts);
    bindMenu();
    mailMenuOnce();
    track('result_shown', true);
    if (!opts.keepScroll) {
      toTop();
      focusEl(resultBox.querySelector('[data-bc-result-title]'));
      countUp(r.score);
    }
  }
  /* The tasting part of the next step: a booking button (Oslo and Akershus) or the samples form (everyone else). */
  function tastingHtml(z) {
    var b = (T.next && T.next.book) || T.book || {};
    if (z !== 'near') return '<div data-bc-samples-slot></div>';
    if (!D.bookingUrl) return '<div data-bc-tasting-slot></div>';
    return '<div class="nh-bc-next__ctas"><a class="nh-btn nh-btn--blue" href="' + esc(D.bookingUrl) +
      '" target="_blank" rel="noopener" data-bc-book>' + esc(b.btn) + '</a></div>' +
      '<p class="nh-bc-small">' + esc(b.note) + '</p>' +
      '<details class="nh-bc-next__more nh-bc-next__more--sub" data-bc-more-sub><summary>' + esc(b.other) +
      '</summary><div data-bc-tasting-slot></div></details>';
  }
  /* The 30-minute meeting: the booking page when there is one, else the request form (same form as the tasting). */
  function meetingHtml() {
    var b = (T.next && T.next.book) || {};
    if (!D.bookingUrl) return '<div data-bc-tasting-slot></div>';
    return '<div class="nh-bc-next__ctas"><a class="nh-btn nh-btn--blue" href="' + esc(D.bookingUrl) +
      '" target="_blank" rel="noopener" data-bc-book>' + esc(b.btnMeet || b.btn) + '</a></div>' +
      '<p class="nh-bc-small">' + esc(b.note) + '</p>' +
      '<details class="nh-bc-next__more nh-bc-next__more--sub" data-bc-more-sub><summary>' + esc(b.other) +
      '</summary><div data-bc-tasting-slot></div></details>';
  }
  /* The menu made for you (Edwin, 1 Nov 2026): always 5 drinks, each of our 5 mixers once, one of them alcohol-free.
     Who makes the drinks sets how complex they are: waiter or runner level 1 (pour and top), bartender up to level 2,
     trained bartender and mixologist up to level 3, the mixologist leaning to level 3. No spirit or modifier twice (ginger
     beer, grapefruit soda, Prosecco, triple sec, bitter aperitif, egg white); Lime Super Juice and soda water may repeat.
     Every combination is scored and the best one wins. When the library has no drink for a mixer at the venue's level,
     the simplest one above it is used (with a penalty), so the menu always has 5 drinks. */
  var LEVEL_TARGET = { waiter: 1, bartender: 2, trained: 2.5, mixologist: 3 };
  var MAY_REPEAT = { mix: 1, limeSJ: 1, soda: 1 };
  var MIXERS = ['TS', 'PV', 'SR', 'ML', 'CT'];
  function pickMenu() {
    var a = state.answers, max = MAX_LEVEL[a.staff] || 2, target = LEVEL_TARGET[a.staff] || 2, blender = a.blender === 'yes';
    var quick = a.staff === 'waiter' || a.venueType === 'nightclub';
    var startPrice = priceFromAnswer();
    function score(d) {
      var sc = 0;
      if (!d.af) {
        if (d.g === a.glass) sc += 3;
        if (a.glass === 'coupe' && d.g === 'rocks') sc += 1;
        if (a.venueType === 'restaurant' && d.s === 'Spritz') sc += 1;
        if (blender && d.s === 'Frozen') sc += 1;
        sc += 1.5 - Math.abs(d.l - target) * 0.75;
        if (quick && d.l === 1) sc += 1;
      }
      if (d.l > max) sc -= 6 * (d.l - max);
      if (startPrice) sc -= Math.max(0, pourCost(d, startPrice) - GREEN) * 20;
      return sc - d.c / 100;
    }
    var cands = {};
    MIXERS.forEach(function (m) {
      cands[m] = DRINKS.filter(function (d) { return d.m === m && !d.af && (blender || d.s !== 'Frozen'); });
    });
    var afs = DRINKS.filter(function (d) { return d.af; });
    var best = null, bestSc = -1e9;
    afs.forEach(function (af) {
      var rest = MIXERS.filter(function (m) { return m !== af.m; });
      (function walk(i, chosen) {
        if (i === rest.length) {
          var all = chosen.concat([af]), used = {}, styles = {}, sc = 0;
          all.forEach(function (d) {
            sc += score(d);
            styles[d.s] = (styles[d.s] || 0) + 1;
            d.p.forEach(function (p) { if (!MAY_REPEAT[p[0]]) { if (used[p[0]]) sc -= 4; used[p[0]] = 1; } });
          });
          Object.keys(styles).forEach(function (k) { if (!/Lemonade/.test(k) && styles[k] > 2) sc -= 3 * (styles[k] - 2); });
          if (sc > bestSc) { bestSc = sc; best = all; }
          return;
        }
        var list = cands[rest[i]];
        if (!list.length) { walk(i + 1, chosen); return; }
        list.forEach(function (d) { walk(i + 1, chosen.concat([d])); });
      })(0, []);
    });
    if (!best) return [];
    var cocktails = best.filter(function (d) { return !d.af; }).sort(function (x, y) { return score(y) - score(x); });
    return cocktails.concat(best.filter(function (d) { return d.af; }));
  }
  function num(x, d) {
    var nb = /^(nb|no|nn)/i.test(document.documentElement.lang || '');
    return x.toLocaleString(nb ? 'nb-NO' : 'en-GB', { minimumFractionDigits: d, maximumFractionDigits: d });
  }
  /* The menu made for you, first on the result page and short enough for the first screen of a phone: one line per drink
     with its pour cost at the venue's price; tap a drink for the recipe. Under it the invite to log in (customise with
     their own spirits), then prices, download, order and the batching guide. */
  function menuHtml(c) {
    var M = T.menu || {}, ing = M.ing || {}, list = pickMenu();
    if (!list.length) return '';
    function nameOf(k, d) { return k === 'mix' ? MIXER_NAME[d.m] : (ing[k] || k); }
    var easy = window.NHDrinkCard ? window.NHDrinkCard.easyLine(list, M) : '';
    var items = list.map(function (d) {
      var spec = d.p.map(function (p) {
        var name = p[0] === 'mix' ? MIXER_NAME[d.m] : (ing[p[0]] || p[0]);
        return esc(name) + ' ' + num(p[1], p[1] % 1 ? 1 : 0) + ' cl';
      }).join(' &middot; ');
      var parts = d.p.filter(function (p) { return /^h/.test(p[0]); }).map(function (p) { return ing[p[0]] || p[0]; });
      parts.push(MIXER_NAME[d.m]);
      d.p.forEach(function (p) { if (/^(soda|tonic|gingerBeer|grapefruitSoda)$/.test(p[0])) parts.push(ing[p[0]] || p[0]); });
      return '<li class="nh-bc-drink" data-bc-drink="' + esc(d.n) + '"><details><summary class="nh-bc-drink__row">' +
        '<span class="nh-bc-drink__main"><span class="nh-bc-drink__name">' + esc(d.n) + '</span>' +
        '<span class="nh-bc-drink__sub">' + (d.af ? esc(M.af) + ' &middot; ' : '') + esc(parts.join(' \u00b7 ')) + '</span></span>' +
        '<span class="nh-bc-drink__pc" data-bc-pc-short></span></summary>' +
        '<div class="nh-bc-drink__body">' +
        (window.NHDrinkCard ? window.NHDrinkCard.html(d, M, nameOf)
          : '<p class="nh-bc-drink__tags">' + esc((M.glass || {})[d.g] || '') + ' &middot; ' + esc((M.level || {})[d.l] || '') + '</p>' +
            '<p class="nh-bc-drink__spec">' + spec + '</p>') +
        '<dl class="nh-bc-drink__nums"><div><dt>' + esc(M.cost) + '</dt><dd>' + num(d.c, 2) + ' kr</dd></div>' +
        '<div><dt data-bc-pc-label></dt><dd data-bc-pc></dd></div></dl>' +
        '<p class="nh-bc-drink__hint" data-bc-hint></p></div></details></li>';
    }).join('');
    var p = menuPrices();
    var prices = '<details class="nh-bc-menu__more"><summary>' + esc(M.changePrices) + '</summary><div class="nh-bc-prices">' +
      '<label>' + esc(M.priceLabel) + '<span><input type="number" inputmode="numeric" min="50" max="999" step="1" data-bc-price="drink" value="' + (p.drink || '') + '" placeholder="179"> ' + esc(M.incl) + '</span></label>' +
      '<label>' + esc(M.afPriceLabel) + '<span><input type="number" inputmode="numeric" min="30" max="999" step="1" data-bc-price="af" value="' + (p.af || '') + '" placeholder="109"> ' + esc(M.incl) + '</span></label>' +
      '</div></details>';
    var made = c && c.venue ? fmt(M.madeFor, { venue: c.venue }) : M.tape;
    /* Main step: the 5 mixers for this menu in the order (venues that can order), or the sign-up first (everyone else).
       Second step: save the menu on the account and choose their own spirits. */
    var kitLine = window.NHDrinkCard ? window.NHDrinkCard.kitLine(list, VOLUME_MID[state.answers.volume] || 0, MIXER_NAME, M.card || {}) : '';
    var invite = '<a class="nh-btn nh-btn--blue nh-bc-menu__go" href="' + esc(D.canOrder ? D.orderUrl : D.applyUrl) + '" data-bc-order-menu>' + esc(M.orderMenu) + '</a>' +
      '<p class="nh-bc-menu__bonus">' + esc(kitLine ? fmt(M.orderKit, { kit: kitLine }) + ' ' : '') + esc(D.canOrder ? M.orderBonus : M.orderApply) + '</p>' +
      '<p class="nh-bc-menu__guarantee"><strong>' + esc(M.guaranteeLead) + '</strong> ' + esc(M.guarantee) + '</p>' +
      '<p class="nh-bc-menu__order-note" data-bc-order-note aria-live="polite"></p>' +
      '<a class="nh-btn nh-bc-menu__save" href="' + esc(D.loggedIn ? D.menuUrl : D.menuLoginUrl) + '" data-bc-invite>' +
      esc(D.loggedIn ? M.customIn : M.custom) + '</a>' +
      '<p class="nh-bc-menu__why">' + esc(D.loggedIn ? M.customNoteIn : M.customNote) + '</p>';
    return '<div class="nh-bc__panel nh-bc-menu" id="nh-bc-menu">' +
      '<p class="nh-eyebrow nh-bc-menu__made">' + esc(made) + '</p>' +
      '<h2 class="nh-display nh-bc-menu__title" tabindex="-1" data-bc-result-title>' + esc(M.title) + '</h2>' +
      (easy ? '<p class="nh-bc-menu__easy">' + esc(easy) + '</p>' : '') +
      '<p class="nh-bc-menu__profit" data-bc-profit aria-live="polite"></p>' +
      '<p class="nh-bc-menu__sum" data-bc-menu-sum aria-live="polite"></p>' +
      '<p class="nh-bc-menu__mailed" data-bc-mailnote aria-live="polite"></p>' +
      '<ol class="nh-bc-menu__list">' + items + '</ol>' +
      '<div class="nh-bc-menu__invite">' + invite + '</div>' +
      '<div class="nh-bc-menu__tools">' + prices +
        '<button type="button" class="nh-bc-menu__link" data-bc-print-menu>' + esc(M.download) + '</button>' +
        '<button type="button" class="nh-bc-menu__link" data-bc-share>' + esc(M.share) + '</button>' +
        '<span class="nh-bc-menu__shared" data-bc-shared aria-live="polite"></span>' +
      '</div>' +
      batchHtml(list) +
      '<p class="nh-bc-small">' + esc(M.note) + '</p></div>';
  }
  /* Pour cost at the venue's own prices. Menu prices include MVA: 25 % on drinks with alcohol, 15 % on alcohol-free.
     The price question gives the starting price; the venue can type its exact prices on the result page. */
  var TARGET = 0.22, GREEN = 0.235; /* green up to 23.5 % (Edwin, 1 Nov 2026); suggested prices still aim at 22 % */
  function priceFromAnswer() { var v = state.answers.price; return /^\d+$/.test(v || '') ? parseInt(v, 10) : 0; }
  function afFrom(drink) { return drink ? Math.max(49, Math.round(drink * 0.6 / 10) * 10 - 1) : 0; }
  function menuPrices() {
    var s = state.prices || {}, drink = s.drink || priceFromAnswer();
    return { drink: drink, af: s.af || afFrom(drink) };
  }
  function pourCost(d, price) { return price ? d.c / (price / (d.af ? 1.15 : 1.25)) : 0; }
  function priceFor(d) { return Math.ceil(d.c / TARGET * (d.af ? 1.15 : 1.25)); }
  function updateMenu() {
    var box = resultBox.querySelector('#nh-bc-menu'), M = T.menu || {};
    if (!box) return;
    var p = menuPrices(), list = pickMenu(), on = 0, sumPc = 0, n = 0;
    Array.prototype.forEach.call(box.querySelectorAll('[data-bc-drink]'), function (li) {
      var d = null; list.forEach(function (x) { if (x.n === li.getAttribute('data-bc-drink')) d = x; });
      if (!d) return;
      var price = d.af ? p.af : p.drink, lab = li.querySelector('[data-bc-pc-label]'), val = li.querySelector('[data-bc-pc]'), hint = li.querySelector('[data-bc-hint]');
      var pill = li.querySelector('[data-bc-pc-short]');
      li.classList.remove('is-on', 'is-over');
      if (!price) {
        lab.textContent = M.price; val.textContent = num(priceFor(d), 0) + ' kr'; hint.textContent = '';
        if (pill) pill.textContent = num(priceFor(d), 0) + ' kr';
        return;
      }
      var pc = pourCost(d, price), ok = pc <= GREEN + 0.0005;
      n += 1; sumPc += pc; if (ok) on += 1;
      lab.textContent = fmt(M.pcAt, { price: num(price, 0) });
      val.textContent = num(pc * 100, 1) + ' %';
      li.classList.add(ok ? 'is-on' : 'is-over');
      hint.textContent = ok ? M.onTarget : fmt(M.over, { price: num(priceFor(d), 0) });
      if (pill) pill.textContent = num(pc * 100, 1) + ' %';
    });
    saveMenu(list, p);
    var sum = box.querySelector('[data-bc-menu-sum]');
    if (sum) sum.textContent = n ? fmt(M.sumAt, { price: num(p.drink || p.af, 0), pc: num(sumPc / n * 100, 1), n: on, total: n }) : M.noPrice;
    var prof = box.querySelector('[data-bc-profit]'), weekly = VOLUME_MID[state.answers.volume] || 0;
    var kr = window.NHDrinkCard ? window.NHDrinkCard.profit(list, p, weekly) : 0;
    /* With a weekly volume the money comes first, in one short paragraph with the pour cost (keeps the menu on the first screen). */
    if (prof) prof.textContent = '';
    if (sum && kr > 0 && n) {
      sum.innerHTML = '<strong>' + esc(fmt(M.profitHead, { kr: num(kr, 0) })) + '</strong> ' +
        esc(fmt(M.profitRest, { price: num(p.drink, 0), n: num(weekly, 0), pc: num(sumPc / n * 100, 1), on: on, total: n }));
    }
  }
  /* Download the menu: the browser's print dialog with only the menu, every drink open (save as PDF). */
  function printMenu() {
    var html = document.documentElement, shut = [];
    Array.prototype.forEach.call(resultBox.querySelectorAll('#nh-bc-menu details'), function (dt) { if (!dt.open) { shut.push(dt); dt.open = true; } });
    html.classList.add('nh-bc-print-menu');
    var done = function () {
      html.classList.remove('nh-bc-print-menu');
      shut.forEach(function (dt) { dt.open = false; });
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    window.print();
    setTimeout(function () { if (html.classList.contains('nh-bc-print-menu')) done(); }, 1500);
  }
  /* The menu is kept in this browser for the logged-in menu page (nh-menu.js, key nh-menu-v1). Spirit choices made there
     stay as long as the drinks are the same. */
  var MENU_KEY = 'nh-menu-v1', API = '/apps/barback', menuSync = null;
  function saveMenu(list, p) {
    try {
      var old = JSON.parse(window.localStorage.getItem(MENU_KEY) || 'null') || {};
      var names = list.map(function (d) { return d.n; });
      var same = old.drinks && old.drinks.map(function (d) { return d.n; }).join('|') === names.join('|');
      var c = state.contact || {};
      window.localStorage.setItem(MENU_KEY, JSON.stringify({
        v: 1, t: Date.now(), venue: c.venue || '', email: c.email || '', vol: VOLUME_MID[state.answers.volume] || 0,
        prices: { drink: p.drink || 0, af: p.af || 0 },
        drinks: list.map(function (d) { return { n: d.n, m: d.m, s: d.s, g: d.g, l: d.l, af: d.af, c: d.c, p: d.p, gar: d.gar || '', v: d.v || '' }; }),
        spirits: same ? (old.spirits || {}) : {}
      }));
    } catch (e) {}
    // Logged-in venues: keep the menu on their account too (the Barback server), so it follows them to every device.
    if (D.loggedIn) {
      clearTimeout(menuSync);
      menuSync = setTimeout(function () {
        var m = null;
        try { m = window.localStorage.getItem(MENU_KEY); } catch (e) {}
        if (m) fetch(API + '/menu', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: m }).catch(function () {});
      }, 800);
    }
  }
  /* "Email me my cocktail menu" (ticked on the last step): sent once, after the result shows, to the address they typed. */
  function mailMenuOnce() {
    var c = state.contact || {}, M = T.menu || {};
    if (!c.emailMenu || !c.email || state.menuMailed) return;
    var m = null;
    try { m = JSON.parse(window.localStorage.getItem(MENU_KEY) || 'null'); } catch (e) {}
    if (!m) return;
    state.menuMailed = true;
    save();
    var note = resultBox.querySelector('[data-bc-mailnote]');
    fetch(API + '/guest-email?lang=' + (D.lang || 'en'), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: c.email, name: c.name || '', lang: D.lang || 'en', menu: m, consent: !!c.consent }) })
      .then(function (r) { return r.json(); })
      .then(function (j) { if (note && j && j.ok) note.textContent = fmt(M.mailed, { email: c.email }); })
      .catch(function () {});
  }
  function bindMenu() {
    var box = resultBox.querySelector('#nh-bc-menu'), M = T.menu || {}, DC = window.NHDrinkCard;
    if (box && !box.hasAttribute('data-bound')) {
      box.setAttribute('data-bound', '');
      Array.prototype.forEach.call(box.querySelectorAll('.nh-bc-drink details'), function (dt) {
        dt.addEventListener('toggle', function () { if (dt.open) track('drink_open', true); });
      });
      var go = box.querySelector('[data-bc-order-menu]');
      if (go) go.addEventListener('click', function (e) {
        track('order_click');
        if (!D.canOrder || !DC) return; /* sign-up first: the link goes there */
        e.preventDefault();
        if (go.getAttribute('aria-busy') === 'true') return;
        go.setAttribute('aria-busy', 'true');
        var label = go.textContent, note = box.querySelector('[data-bc-order-note]');
        go.textContent = M.orderBusy;
        DC.orderMenu(pickMenu(), VOLUME_MID[state.answers.volume] || 0, D.orderUrl, MIXER_NAME)
          .then(function (url) { location.href = url; })
          .catch(function () { go.textContent = label; go.removeAttribute('aria-busy'); if (note) note.textContent = M.orderFail; location.href = D.orderUrl; });
      });
      var inv = box.querySelector('[data-bc-invite]');
      if (inv) inv.addEventListener('click', function () { track('save_click'); });
      var sh = box.querySelector('[data-bc-share]');
      if (sh && DC) sh.addEventListener('click', function () {
        track('share_click');
        var menu = null;
        try { menu = JSON.parse(window.localStorage.getItem(MENU_KEY) || 'null'); } catch (err) {}
        if (!menu) return;
        var title = fmt(M.shareTitle, { venue: menu.venue || '' }).replace(/\s+$/, '');
        DC.share(DC.shareLink(menu, location.origin + D.menuUrl), title, M.shareText, function (how) {
          var out = box.querySelector('[data-bc-shared]');
          if (out && how === 'copied') out.textContent = M.copied;
        });
      });
    }
    Array.prototype.forEach.call(resultBox.querySelectorAll('[data-bc-price]'), function (inp) {
      inp.addEventListener('input', function () {
        var v = parseInt(inp.value, 10);
        state.prices = state.prices || {};
        state.prices[inp.getAttribute('data-bc-price')] = v > 0 ? v : 0;
        save(); updateMenu();
      });
    });
    updateMenu();
  }
  /* Batching guide for the menu, optional and closed by default. Batched drinks go out about 3 times faster:
     60 seconds from scratch, 20 seconds batched, so 40 seconds saved per cocktail. Bubbles, egg white and ice are added when serving. */
  var VOLUME_MID = { 'under-50': 40, '50-100': 75, '100-200': 150, 'over-200': 250 };
  var AT_SERVICE = { soda: 1, tonic: 1, gingerBeer: 1, grapefruitSoda: 1, hProsecco: 1, egg: 1 };
  function batchHtml(list) {
    var B = (T.menu && T.menu.batch) || {}, ing = (T.menu && T.menu.ing) || {};
    if (!B.summary) return '';
    var rows = list.map(function (d) {
      var base = [], serve = [], baseCl = 0;
      d.p.forEach(function (p) {
        var name = p[0] === 'mix' ? MIXER_NAME[d.m] : (ing[p[0]] || p[0]);
        var txt = esc(name) + ' ' + num(p[1], p[1] % 1 ? 1 : 0) + ' cl';
        if (AT_SERVICE[p[0]]) serve.push(txt); else { base.push(txt); baseCl += p[1]; }
      });
      if (base.length < 2) return '';
      if (d.s === 'Frozen') serve.push(esc(B.frozen));
      else if (!serve.length) serve.push(esc(B.ice));
      return '<li class="nh-bc-batch__drink"><strong>' + esc(d.n) + '</strong>' +
        '<span><b>' + esc(B.base) + ':</b> ' + base.join(' &middot; ') + '</span>' +
        '<span><b>' + esc(B.serve) + ':</b> ' + serve.join(' &middot; ') + '</span>' +
        '<span class="nh-bc-batch__litre">' + esc(fmt(B.litre, { n: num(Math.floor(100 / baseCl), 0) })) + '</span></li>';
    }).join('');
    var n = VOLUME_MID[state.answers.volume];
    var saved = '';
    if (n) {
      var mins = Math.round(n * 40 / 60 / 10) * 10, h = Math.floor(mins / 60), m = mins % 60;
      var t = (h ? h + ' ' + (h === 1 ? B.hour : B.hours) : '') + (h && m ? ' ' : '') + (m ? m + ' ' + B.minutes : '');
      saved = '<p class="nh-bc-batch__saved">' + esc(fmt(B.saved, { n: num(n, 0), time: t })) + '</p>';
    }
    var needs = (B.needs || []).map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('');
    return '<details class="nh-bc-batch" data-bc-batch><summary>' + esc(B.summary) + '</summary>' +
      '<div class="nh-bc-batch__body"><p class="nh-bc-batch__lead">' + esc(B.lead) + '</p>' + saved +
      '<ul class="nh-bc-batch__list">' + rows + '</ul>' +
      '<h3 class="nh-bc-batch__h">' + esc(B.needsTitle) + '</h3><ul class="nh-bc-batch__needs">' + needs + '</ul>' +
      '<p class="nh-bc-small">' + esc(B.note) + '</p></div></details>';
  }
  function nextHtml(r, c) {
    var n = (T.next && T.next[r.seg]) || {};
    var acct = ORDER_URL;
    var z = zone(c);
    var h = '<div class="nh-bc__panel nh-bc-next nh-bc-next--' + r.seg + '" id="nh-bc-next">' +
      '<h2 class="nh-display nh-bc-next__title">' + esc(n.title) + '</h2>';
    if (r.seg === 'hot') {
      var text = z === 'near' ? (D.bookingUrl ? n.textNear : (n.textNearForm || n.textNear)) :
        (z === 'abroad' ? (n.textAbroad || n.textFar) : n.textFar);
      h += '<p>' + esc(text) + '</p>' + meetingHtml() +
        '<p class="nh-bc-next__alt"><a href="' + esc(acct) + '" data-bc-order>' + esc(n.alt) + ' &rarr;</a></p>';
    } else if (r.seg === 'warm') {
      var alt = z === 'near' ? (D.bookingUrl ? n.alt : (n.altForm || n.alt)) : (n.altFar || n.alt);
      h += '<p>' + esc(n.text) + '</p><div class="nh-bc-next__ctas"><a class="nh-btn nh-btn--blue" href="' + esc(acct) + '" data-bc-order>' + esc(n.btn) + '</a></div>' +
        '<details class="nh-bc-next__more" data-bc-more><summary>' + esc(alt) + '</summary>' + tastingHtml(z) + '</details>';
    } else {
      h += '<p>' + esc(n.text) + '</p><div class="nh-bc-next__ctas">' +
        '<a class="nh-btn nh-btn--blue" href="' + esc(D.guideUrl) + '">' + esc(n.btn) + '</a>' +
        '<a class="nh-btn" href="' + esc(D.pricelistUrl) + '" target="_blank" rel="noopener">' + esc(n.btn2) + '</a></div>' +
        '<p class="nh-bc-next__alt"><a href="' + esc(acct) + '" data-bc-order>' + esc(n.alt) + ' &rarr;</a></p>';
    }
    return h + '</div>';
  }
  function fillCopies(el, copy) {
    Array.prototype.forEach.call(el.querySelectorAll('[data-bc-copy]'), function (f) {
      var k = f.getAttribute('data-bc-copy');
      if (k in copy && (f.type === 'hidden' || !f.value)) f.value = copy[k];
    });
  }
  function showSent(part, box) {
    if (part.form) part.form.hidden = true;
    var ok = part.el.querySelector(part.ok);
    if (ok) ok.hidden = false;
    Array.prototype.forEach.call(resultBox.querySelectorAll('details'), function (d) { if (d.contains(part.el)) d.open = true; });
    setTimeout(function () {
      var b = resultBox.querySelector(box);
      if (b) b.scrollIntoView({ block: 'center' });
    }, 60);
  }
  function mountParts(r, c, opts) {
    var copy = {
      venue: c.venue || '', name: c.name || '', email: c.email || '', phone: c.phone || '',
      location: c.location || '', score: r.score + ' % (' + BAND_LABEL[r.band] + ')'
    };
    var slots = [
      { part: PARTS[0], slot: '[data-bc-tasting-slot]', sent: opts.tastingSent, box: '#nh-bc-next' },
      { part: PARTS[1], slot: '[data-bc-samples-slot]', sent: opts.samplesSent, box: '#nh-bc-next' },
      { part: PARTS[2], slot: '[data-bc-docs-slot]', sent: opts.docsSent, box: '#nh-bc-docs' }
    ];
    slots.forEach(function (s) {
      var slot = resultBox.querySelector(s.slot);
      if (!slot || !s.part.el) return;
      slot.appendChild(s.part.el);
      fillCopies(s.part.el, copy);
      if (s.sent) showSent(s.part, s.box);
    });
    // Super Juice is sold in Norway only: no Super Juice samples abroad.
    var sj = samples ? samples.querySelector('[data-bc-sj]') : null;
    if (sj) {
      var abroad = zone(c) === 'abroad', box = sj.querySelector('input');
      sj.hidden = abroad;
      if (box) { box.disabled = abroad; box.checked = !abroad; }
    }
  }
  function unmountParts() {
    if (!tpl) return;
    PARTS.forEach(function (p) {
      if (!p.el) return;
      tpl.appendChild(p.el);
      if (p.form) p.form.hidden = false;
      var ok = p.el.querySelector(p.ok);
      if (ok) ok.hidden = true;
    });
  }
  function renderFallback() {
    show('result');
    resultBox.innerHTML = '<div class="nh-bc__panel nh-bc-score"><h2 class="nh-display nh-bc-score__label" tabindex="-1" data-bc-result-title>' +
      esc(T.fallbackTitle) + '</h2><p>' + esc(T.fallbackText) + '</p><div class="nh-bc-result__actions">' +
      '<button type="button" class="nh-btn nh-btn--sm nh-btn--blue" data-bc-retake>' + esc(T.retake) + '</button></div></div>';
    focusEl(resultBox.querySelector('[data-bc-result-title]'));
  }
  /* Carry the visitor's details to the quick account form (snippet nh-bc-prefill reads them there). */
  function rememberForOrder() {
    var c = state.contact || {};
    if (!c.email) return;
    try {
      localStorage.setItem(PREFILL_KEY, JSON.stringify({ n: c.name || '', e: c.email || '', p: c.phone || '', v: c.venue || '', t: Date.now() }));
    } catch (e) {}
  }

  /* ---------- events ---------- */
  var busy = false;
  Array.prototype.forEach.call(document.querySelectorAll('[data-bc-start]'), function (a) {
    a.addEventListener('click', function (e) { e.preventDefault(); start(); });
  });
  /* Price field: digits only (letters never appear), 50 to 999 kr, then on to the next question. */
  stepBox.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || !t.hasAttribute || !t.hasAttribute('data-bc-num-in')) return;
    var clean = t.value.replace(/\D+/g, '').slice(0, 3);
    if (clean !== t.value) t.value = clean;
    var err = stepBox.querySelector('[data-bc-num-err]');
    if (err) err.textContent = '';
  });
  stepBox.addEventListener('submit', function (e) {
    var f = e.target;
    if (!f || !f.hasAttribute || !f.hasAttribute('data-bc-num')) return;
    e.preventDefault();
    if (busy) return;
    var fs = f.closest('[data-q]'), inp = f.querySelector('[data-bc-num-in]'), v = parseInt(inp.value, 10);
    var qt = (T.qualifiers && T.qualifiers[fs.getAttribute('data-q')]) || {};
    if (!(v >= 50 && v <= 999)) {
      var err = f.querySelector('[data-bc-num-err]');
      if (err) err.textContent = qt.err || '';
      focusEl(inp);
      return;
    }
    state.answers[fs.getAttribute('data-q')] = String(v);
    if (state.prices) { state.prices.drink = 0; state.prices.af = 0; }
    busy = true;
    setTimeout(function () {
      busy = false;
      state.step += 1;
      save();
      fillOutputs();
      renderStep();
    }, 120);
  });
  stepBox.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('.nh-bc-opt') : null;
    if (!b || busy) return;
    var fs = b.closest('[data-q]');
    if (!fs) return;
    state.answers[fs.getAttribute('data-q')] = b.getAttribute('data-v');
    Array.prototype.forEach.call(fs.querySelectorAll('.nh-bc-opt'), function (x) {
      x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
    });
    busy = true;
    setTimeout(function () {
      busy = false;
      state.step += 1;
      save();
      fillOutputs();
      renderStep();
    }, 220);
  });
  Array.prototype.forEach.call(app.querySelectorAll('[data-bc-back]'), function (b) {
    b.addEventListener('click', function () {
      if (!gate.hidden) { state.step = TOTAL - 1; save(); renderStep(); return; }
      if (state.step <= 0) { show('intro'); window.scrollTo(0, 0); return; }
      state.step -= 1;
      save();
      renderStep();
    });
  });
  form.addEventListener('input', function () { readContact(); save(); });
  form.addEventListener('change', function () { readContact(); save(); });
  form.addEventListener('submit', function () {
    readContact();
    fillOutputs();
    state.sent = true;
    save();
    setBusy(form.querySelector('[data-bc-submit]'), true);
  });
  [[tastingForm, '[data-bc-tasting-submit]'], [samplesForm, '[data-bc-samples-submit]'], [docsForm, '[data-bc-docs-submit]']].forEach(function (f) {
    if (!f[0]) return;
    f[0].addEventListener('submit', function () {
      var ph = f[0].querySelector('input[name="contact[phone]"]');
      if (ph && ph.value) { state.contact = state.contact || {}; state.contact.phone = ph.value; save(); }
      setBusy(f[0].querySelector(f[1]), true);
    });
  });
  resultBox.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-bc-order]')) { rememberForOrder(); return; }
    var t = e.target.closest ? e.target.closest('[data-bc-retake],[data-bc-print],[data-bc-print-menu]') : null;
    if (!t) return;
    if (t.hasAttribute('data-bc-print')) { window.print(); return; }
    if (t.hasAttribute('data-bc-print-menu')) { printMenu(); return; }
    unmountParts();
    state = fresh(state.contact);
    save();
    renderStep();
  });
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-label]'), function (b) { setBusy(b, false); });
  });

  /* ---------- start-up ---------- */
  var state = load() || fresh();
  if (DESIGN) { show('intro'); return; }
  var q = params();
  var docsSent = q.docs === 'sent' || q.customer_posted === 'true';
  var returned = q.check === 'sent' || q.tasting === 'sent' || q.samples === 'sent' || docsSent || q.contact_posted === 'true';
  // Tidy the address: drop the helper parameters once read (shared links then carry no source or status).
  try {
    var keep = location.search.replace(/^\?/, '').split('&').filter(function (kv) {
      return kv && !/^(kilde|utm_[a-z]+|check|tasting|samples|docs|contact_posted|customer_posted|form_type)(=|$)/i.test(kv);
    });
    var clean = location.pathname + (keep.length ? '?' + keep.join('&') : '');
    if (clean !== location.pathname + location.search + location.hash) history.replaceState(history.state, '', clean);
  } catch (e) {}
  if (returned) {
    if (state.sent && complete()) {
      renderResult({ tastingSent: q.tasting === 'sent', samplesSent: q.samples === 'sent', docsSent: docsSent, keepScroll: q.check !== 'sent' });
    }
    else renderFallback();
  } else {
    show('intro');
  }
})();
